import { sendSnapshotId } from './snapshot-delivery';
import { onCLS, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';
import { acquisition, activeDelta, safeDimension, sessionExpired } from './metrics';
/**
 * QCT Analytics Standalone SDK
 */

import {
  IGNORE_SELECTOR,
  redactDocumentHtml,
  redactPath,
  redactUrl,
  scrubSelector,
  scrubText,
} from './redact';

/**
 * Hooks a page can set on `window` to control recording (read before every event):
 * - `qctShouldTrack(url)`: return false and nothing is recorded for that address,
 *   e.g. while a consent banner has no "yes", or on pages that must never be recorded.
 * - `qctDisabled = true`: the tracker records nothing at all on this page load
 *   (set by Leaps' own server-side page captures so they are not counted as visits).
 */
interface TrackerHooks {
  qctShouldTrack?: (url: string) => boolean;
  qctDisabled?: boolean;
}

interface AnalyticsConfig {
  apiKey: string;
  endpoint?: string;
  debug?: boolean;
  sampleRate?: number; // 0.0 to 1.0 (e.g. 0.1 for 10%)
}

interface AnalyticsEvent {
  type: string;
  url: string;
  path: string;
  timestamp: number;
  data?: Record<string, unknown>;
  userId?: string;
  email?: string;
  sessionId?: string;
}

export class QCTTracker {
  private config: AnalyticsConfig | null = null;
  private sessionId: string = '';
  private visitorId: string = '';
  private userId: string | null = null;
  private email: string | null = null;
  private eventQueue: AnalyticsEvent[] = [];
  private isProcessing: boolean = false;
  private BATCH_SIZE = 10;
  private FLUSH_INTERVAL = 5000;
  private initialized = false;
  private excluded = false; // If true, do not track anything for this session
  private optedOut = false; // Set by optOut(): nothing is recorded or sent until optIn()

  // Rage Click Tracking
  private clickHistory: Map<string, number[]> = new Map();
  private RAGE_CLICK_THRESHOLD = 5;
  private RAGE_CLICK_WINDOW = 2000;

  // Throttling
  private eventCount = 0;
  private throttleStartTime = Date.now();
  private THROTTLE_LIMIT = 50; // max events
  private THROTTLE_WINDOW = 10000; // per 10 seconds


  // Scroll Tracking State
  private snapshotIds = new Map<string, string>();
  private snapshotRequests = new Map<string, number>();
  private lastInteraction = Date.now();
  private lastActiveTick = Date.now();
  private performanceStarted = false;
  private consentGeneration = 0;
  private maxScroll = 0;
  private scrollTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      this.initSession();
      this.setupListeners();
      setInterval(() => this.flush(), this.FLUSH_INTERVAL);
      setInterval(() => this.recordActiveTime(), 15000);
      for (const name of ['pointerdown', 'keydown', 'scroll']) {
        document.addEventListener(name, () => {
          this.recordActiveTime();
          if (this.canRecord()) {
            this.renewSession();
            this.lastInteraction = Date.now();
            this.saveActivity();
          }
        }, { passive: true });
      }
      
      // Auto-init if script tag has data attrs
      this.autoInit();
    }
  }

  private autoInit() {
     // Find the script tag that loaded this file (e.g., qct.js)
     // We look for any script with data-apikey
     const scripts = document.getElementsByTagName('script');
     let myScript: HTMLScriptElement | null = null;
     
     for (let i = 0; i < scripts.length; i++) {
         if (scripts[i].getAttribute('data-apikey')) {
             myScript = scripts[i];
             break;
         }
     }

     if (myScript) {
         const apiKey = myScript.getAttribute('data-apikey');
         // Default to the server that served this script (e.g. https://leaps.quantumculture.in/qct.js → /api/analytics/collect)
         const scriptOrigin = myScript.src ? new URL(myScript.src, window.location.href).origin : window.location.origin;
         const endpoint = myScript.getAttribute('data-endpoint') || `${scriptOrigin}/api/analytics/collect`;
         const debug = myScript.getAttribute('data-debug') === 'true';
         
         if (apiKey) {
             this.init({ apiKey, endpoint, debug });
         }
     }
  }

  public init(config: AnalyticsConfig) {
    if (this.initialized) return;
    
    if (!config.endpoint) {
      console.warn('[Leaps] Analytics not started: no endpoint configured (set data-endpoint on the script tag).');
      return;
    }

    this.config = {
      sampleRate: 1.0, // Default to 100% tracking
      ...config,
    };
    this.initialized = true;
    this.lastActiveTick = this.lastInteraction = Date.now();

    // Sampling Logic
    if (this.config.sampleRate !== undefined && this.config.sampleRate < 1.0) {
        if (Math.random() > this.config.sampleRate) {
            this.excluded = true;
            if (this.config.debug) console.log('[QCT] Session Excluded via Sampling');
            return;
        }
    }
    
    if (this.config.debug) console.log('[QCT] Initialized', this.config);
    this.track('page_view');
    this.startPerformance();
    // Configuration must exist before the initial page copy; the constructor's listeners run earlier.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => void this.trackSnapshot(), { once: true });
    } else {
      void this.trackSnapshot();
    }
  }

  public identify(userId: string, email: string) {
    if (this.optedOut) return;
    this.userId = userId;
    this.email = email;
    if (this.config?.debug) console.log('[QCT] Identified', { userId });
  }

  /**
   * Stop recording and forget this visitor, e.g. when they withdraw consent: drops
   * anything not yet sent, the identify() details, and the visitor and session ids
   * kept in browser storage. optIn() starts again with new ids.
   */
  public optOut() {
    this.optedOut = true;
    this.consentGeneration++;
    this.snapshotIds.clear();
    this.snapshotRequests.clear();
    this.lastActiveTick = Date.now();
    this.eventQueue = [];
    this.userId = null;
    this.email = null;
    this.visitorId = '';
    this.sessionId = '';
    try {
      localStorage.removeItem('qct_vid');
    } catch {
      /* storage blocked: nothing was stored */
    }
    try {
      sessionStorage.removeItem('qct_sid');
      sessionStorage.removeItem('qct_last_activity');
      // Markers for page copies already sent this session
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key && key.indexOf('qct_snap_') === 0) sessionStorage.removeItem(key);
      }
    } catch {
      /* storage blocked: nothing was stored */
    }
  }

  /** Undo optOut(): new visitor and session ids, then a page view for the current page. */
  public optIn() {
    if (!this.optedOut) return;
    this.optedOut = false;
    this.initSession();
    this.lastInteraction = this.lastActiveTick = Date.now();
    this.track('page_view');
    this.startPerformance();
    void this.trackSnapshot();
  }

  /** False after optOut(), or when the page's qctDisabled / qctShouldTrack hooks say no for the current address. */
  private canRecord(): boolean {
    if (this.optedOut) return false;
    const hooks = window as unknown as TrackerHooks;
    if (hooks.qctDisabled === true) return false;
    if (typeof hooks.qctShouldTrack === 'function') {
      try {
        return hooks.qctShouldTrack(window.location.href) !== false;
      } catch {
        return false; // a broken hook must not mean "record everything"
      }
    }
    return true;
  }

  private initSession() {
    // Storage can be blocked (private mode, strict settings): fall back to ids that last for this page only.
    let vid: string | null = null;
    try {
      vid = localStorage.getItem('qct_vid');
      if (!vid) {
        vid = this.uuid();
        localStorage.setItem('qct_vid', vid);
      }
    } catch {
      /* storage blocked */
    }
    this.visitorId = vid || this.uuid();

    let sid: string | null = null;
    try {
      sid = sessionStorage.getItem('qct_sid');
      const activity = Number(sessionStorage.getItem('qct_last_activity'));
      if (sessionExpired(activity, Date.now())) sid = null;
      if (!sid) {
        sid = this.uuid();
        sessionStorage.setItem('qct_sid', sid);
      }
    } catch {
      /* storage blocked */
    }
    this.sessionId = sid || this.uuid();
    this.saveActivity();
  }

  private saveActivity() {
    try { sessionStorage.setItem('qct_last_activity', String(this.lastInteraction)); } catch { /* unavailable */ }
  }

  private renewSession() {
    if (!sessionExpired(this.lastInteraction, Date.now())) return;
    this.sessionId = this.uuid();
    try { sessionStorage.setItem('qct_sid', this.sessionId); } catch { /* unavailable */ }
  }

  private recordActiveTime() {
    const now = Date.now();
    const activeMs = activeDelta(this.lastActiveTick, now, this.lastInteraction,
      this.initialized && !this.excluded && this.canRecord() && document.visibilityState === 'visible');
    this.lastActiveTick = now;
    if (activeMs > 0) this.track('active_time', { activeMs: Math.round(activeMs) });
  }

  private startPerformance() {
    if (this.performanceStarted || !this.canRecord() || this.excluded) return;
    this.performanceStarted = true;
    const generation = this.consentGeneration;
    const address = window.location.href;
    const report = (metric: Metric) => {
      // Never revive a metric after consent withdrawal, or attribute hard-navigation metrics to a later SPA page.
      if (generation !== this.consentGeneration || !this.canRecord()) return;
      const hook = (window as unknown as TrackerHooks).qctShouldTrack;
      try { if (hook && hook(address) === false) return; } catch { return; }
      this.track('web_vital', {
        name: metric.name, value: metric.value, id: metric.id,
        rating: metric.rating, navigationType: metric.navigationType,
      }, address);
      void this.flush();
    };
    onLCP(report); onCLS(report); onINP(report); onTTFB(report);
  }

  /** Explicit safe funnel steps. Never pass field values or personal identifiers. */
  public form(name: string, step: 'start' | 'submit' | 'success' | 'error') {
    const form = safeDimension(name);
    if (form && ['start', 'submit', 'success', 'error'].includes(step)) this.track('form_step', { form, step });
  }
  public conversion(name: string) {
    const goal = safeDimension(name);
    if (goal) this.track('conversion', { goal });
  }

  private hashString(str: string): string {
      let hash = 0;
      if (str.length === 0) return '0';
      for (let i = 0; i < str.length; i++) {
          const char = str.charCodeAt(i);
          hash = ((hash << 5) - hash) + char;
          hash = hash & hash; // Convert to 32bit integer
      }
      return hash.toString();
  }

  private async trackSnapshot() {
      if (typeof window === 'undefined') return;
      if (this.excluded || !this.canRecord()) return;
      // Never snapshot our own heatmap viewer (same exclusion as track()).
      if (window.location.pathname.startsWith('/dashboard/heatmaps')) return;

      // Same path redaction as track(), so page copies and events still line up.
      const path = redactPath(window.location.pathname) || '/';
      const requestVersion = (this.snapshotRequests.get(path) || 0) + 1;
      this.snapshotRequests.set(path, requestVersion);
      this.snapshotIds.delete(path);
      // PRIVACY: the page is serialised from a CLONE of the document with every
      // visitor-entered value removed first (see ./redact) -- input/textarea/select
      // values, contenteditable content, scripts, inline event handlers and any
      // [data-leaps-ignore] region. The unredacted DOM never leaves the browser and
      // the live page is never modified. Layout-affecting markup, CSS, classes and
      // image sources are kept so the heatmap still lines up.
      const generation = this.consentGeneration;
      const html = redactDocumentHtml(document);
      if (!html) return;
      const hash = this.hashString(html);
      const key = `qct_snap_${path}_${hash}`;
      
      try {
        const cached = sessionStorage.getItem(key);
        if (cached && /^[a-f0-9]{24}$/i.test(cached)) { this.snapshotIds.set(path, cached); return; }
      } catch { /* storage unavailable */ }
      
      // Use a separate direct fetch for snapshots to avoid blocking the event queue 
      // and to handle larger payloads (Beacon has 64KB limit)
      if (this.config && this.config.endpoint) {
          try {
             const snapshotEndpoint = this.config.endpoint.replace('/collect', '/snapshot');
             
             const accepted = await sendSnapshotId(snapshotEndpoint, JSON.stringify({
                 apiKey: this.config.apiKey,
                 path,
                 hash,
                 html,
             }));
             if (accepted) {
                 if (generation !== this.consentGeneration || !this.canRecord() || this.snapshotRequests.get(path) !== requestVersion) return;
                 this.snapshotIds.set(path, accepted);
                 try { sessionStorage.setItem(key, accepted); } catch { /* storage unavailable */ }
             }

          } catch (e) {
              console.error('Snapshot failed', e);
          }
      }
  }

  public track(eventType: string, data: Record<string, unknown> = {}, address = window.location.href) {
    if (!this.initialized || !this.config || this.excluded || !this.canRecord()) return;

    // Exclude internal analytics pages
    if (window.location.pathname.startsWith('/dashboard/heatmaps')) return;
    
    if (['page_view', 'click', 'rage_click', 'form_step', 'conversion'].includes(eventType)) {
      this.renewSession();
      this.lastInteraction = Date.now();
      this.saveActivity();
    }
    // Check Throttling
    const now = Date.now();
    if (now - this.throttleStartTime > this.THROTTLE_WINDOW) {
        this.eventCount = 0;
        this.throttleStartTime = now;
    }
    
    if (this.eventCount >= this.THROTTLE_LIMIT) {
        if (this.config.debug && this.eventCount === this.THROTTLE_LIMIT) {
            console.warn('[QCT] Throttling events (limit exceeded)');
        }
        this.eventCount++;
        return;
    }
    this.eventCount++;

    const event: AnalyticsEvent = {
        type: eventType,
        // Query strings and hash fragments routinely carry one-time codes, reset
        // tokens, e-mail addresses and phone numbers; those values (and path
        // segments that are an e-mail address or a long number) are replaced
        // before sending.
        url: redactUrl(address),
        path: redactPath(new URL(address).pathname) || '/',
        sessionId: this.sessionId,
        timestamp: Date.now(),
        data: {
          ...data,
          w: Number.isFinite(window.innerWidth) && window.innerWidth > 0 ? window.innerWidth : undefined,
          h: Number.isFinite(window.innerHeight) && window.innerHeight > 0 ? window.innerHeight : undefined,
          ...(eventType === 'page_view' ? acquisition(address, document.referrer) : {}),
          ...(eventType === 'click' && this.snapshotIds.get(redactPath(window.location.pathname) || '/') ? { snapshotId: this.snapshotIds.get(redactPath(window.location.pathname) || '/') } : {}),
          device: this.getDeviceType()
        },
    };
    if (this.userId) event.userId = this.userId;
    if (this.email) event.email = this.email;

    this.eventQueue.push(event);
    if (this.config.debug) console.log('[QCT] Tracked', eventType);

    if (this.eventQueue.length >= this.BATCH_SIZE) {
      this.flush();
    }
  }

  // Helper: Capture current scroll depth
  private captureScrollDepth() {
      const h = document.documentElement;
      if (!h) return;
      const scrollTop = h.scrollTop || document.body.scrollTop;
      const scrollHeight = h.scrollHeight || document.body.scrollHeight;
      const clientHeight = h.clientHeight;
      if (scrollHeight <= clientHeight) return; // No scroll possible

      const scrolled = (scrollTop + clientHeight) / scrollHeight;
      const percentage = Math.round(scrolled * 100);
      
      if (percentage > this.maxScroll) {
          this.maxScroll = percentage;
      }
  }

  // Helper: Send scroll event and reset
  private trackScrollEvent() {
      // Capture one last time before sending
      this.captureScrollDepth();
      
      if (this.maxScroll > 0) {
          this.track('scroll', { scrollDepth: this.maxScroll });
      }
      // Reset for next page/view
      this.maxScroll = 0;
      this.captureScrollDepth(); // Initialize for new page (likely 0 or 100 if short)
  }

  private setupListeners() {
    const originalPushState = history.pushState;
    history.pushState = (...args) => {
      // Track activity and scroll for the page we are LEAVING
      this.recordActiveTime();
      this.trackScrollEvent();

      originalPushState.apply(history, args);
      
      // Track new page view
      this.track('page_view');
      this.trackSnapshot();
    };

    window.addEventListener('popstate', () => {
        // Popstate happens AFTER url change usually, but we want to reset scroll for the new view
        // Ideally we'd track the OLD view's scroll, but popstate implementation varies.
        // For simplicity, we assume we just arrived at a new page. 
        // We should validly track the previous page's scroll if possible. 
        // But since popstate is browser-driven, the "previous" state might be lost.
        // However, `maxScroll` still holds the value from the previous interaction.
        this.trackScrollEvent(); 
        
        this.track('page_view');
        this.trackSnapshot();
    });
    
    // Click Tracking
    document.addEventListener('click', (e) => {
      const originalTarget = e.target as HTMLElement;

      // Privacy: customers can add data-leaps-ignore (or the legacy data-qct-ignore) to an
      // element to exclude it (and its children) from recording -- covers clicks, rage-click
      // detection, and input interactions, all of which are captured through this one handler.
      if (originalTarget.closest?.(IGNORE_SELECTOR)) return;

      const target = originalTarget.closest('button, a, input, select, textarea, [role="button"]') as HTMLElement || originalTarget;

      // The click label must never carry what the visitor typed: form controls are
      // described by their type/name, editable regions are not described at all, and
      // any remaining free text is scrubbed of e-mail addresses, long digit runs and
      // opaque tokens before it is queued.
      let text = target.getAttribute('data-tracking-label') || '';
      if (!text) {
          if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
              const type = target.getAttribute('type') || (target instanceof HTMLTextAreaElement ? 'textarea' : 'text');
              const name = target.name || target.id || 'unknown_input';
              text = `[INPUT:${type}:${name}]`;
          } else if (target instanceof HTMLSelectElement) {
              // innerText of a <select> is its option list, which can be visitor-specific.
              text = `[SELECT:${target.name || target.id || 'unknown_select'}]`;
          } else if (originalTarget.closest?.('[contenteditable]:not([contenteditable="false"]), [role="textbox"]')) {
              text = '[EDITABLE]';
          } else {
              text = scrubText(target.getAttribute('aria-label') || target.title || target.innerText || '', 50);
          }
      } else {
          text = scrubText(text, 50);
      }

      const sectionEl = originalTarget.closest('[data-section]');
      const section = sectionEl ? sectionEl.getAttribute('data-section') : 'global';
      
      const rect = target.getBoundingClientRect();
      const rx = (e.clientX - rect.left) / rect.width;
      const ry = (e.clientY - rect.top) / rect.height;
      const selector = scrubSelector(this.getSelector(target));

      this.track('click', {
        x: e.clientX,
        y: e.clientY + window.scrollY,
        w: window.innerWidth,
        h: window.innerHeight,
        selector: selector,
        rx: rx.toFixed(4),
        ry: ry.toFixed(4),
        text: text.trim(),
        section: section,
        tagName: target.tagName,
        device: this.getDeviceType(),
        elementWidth: rect.width,
        elementHeight: rect.height
      });
      
      this.checkRageClick(selector, e.clientX, e.clientY);
    });

    // Scroll Tracking
    window.addEventListener('scroll', () => {
       if (this.scrollTimer) return;
       this.scrollTimer = setTimeout(() => { 
           this.captureScrollDepth(); 
           this.scrollTimer = null; 
       }, 500); 
    });

    // Handle visibility change
    document.addEventListener('visibilitychange', () => {
        // Include the final visible interval; hidden time is never accumulated.
        if (document.visibilityState === 'hidden') {
          const now = Date.now();
          const activeMs = activeDelta(this.lastActiveTick, now, this.lastInteraction, this.canRecord());
          if (activeMs > 0) this.track('active_time', { activeMs: Math.round(activeMs) });
        }
        this.lastActiveTick = Date.now();
        if (document.visibilityState === 'hidden') {
            this.captureScrollDepth();
            if (this.maxScroll > 0) {
                this.track('scroll', { scrollDepth: this.maxScroll });
                // Do NOT reset maxScroll here, user might come back to SAME page
            }
            void this.flush();
        }
    });

    // Fallback for older browsers
    window.addEventListener('beforeunload', () => {
        this.trackScrollEvent(); // Send and reset (though we are leaving)
        this.flush();
    });

    // Errors. Messages and stacks routinely echo back what the visitor typed
    // ("Invalid email: someone@example.com"), so they are scrubbed like any
    // other free text, and the failing script URL loses its query secrets.
    window.addEventListener('error', (e) => {
      this.track('crash', {
        message: scrubText(e.message, 500),
        filename: redactUrl(e.filename),
        lineno: e.lineno,
        colno: e.colno,
        stack: scrubText(e.error?.stack, 2000)
      });
    });

    window.addEventListener('unhandledrejection', (e) => {
        const raw = e.reason && typeof e.reason === 'object' ? e.reason.message : e.reason;
        this.track('crash', {
            type: 'unhandled_rejection',
            reason: scrubText(raw == null ? '' : String(raw), 500),
            stack: scrubText(e.reason?.stack, 2000)
        });
    });
  }

  private async flush() {
    if (this.optedOut || this.eventQueue.length === 0 || !this.config) return;
    if (this.isProcessing) return;

    this.isProcessing = true;
    // Queue entries keep their original session through inactivity rollover and in-flight requests.
    const batchSession = this.eventQueue[0].sessionId || this.sessionId;
    const batch = this.eventQueue.filter(event => (event.sessionId || this.sessionId) === batchSession);
    this.eventQueue = this.eventQueue.filter(event => (event.sessionId || this.sessionId) !== batchSession);

    const payload = {
      apiKey: this.config.apiKey,
      sessionId: batchSession,
      visitorId: this.visitorId,
      events: batch.map(({ sessionId, ...event }) => { void sessionId; return event; }),
    };

    try {
        // Use Beacon for reliability on unload
        if (navigator.sendBeacon && document.visibilityState === 'hidden') {
             const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
             if (!navigator.sendBeacon(this.config.endpoint!, blob)) {
               await fetch(this.config.endpoint!, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), keepalive: true });
             }
        } else {
            await fetch(this.config.endpoint!, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                keepalive: true,
            });
        }
    } catch (e) {
      if (this.config.debug) console.error('[QCT] Flush Failed', e);
    } finally {
      this.isProcessing = false;
      if (this.eventQueue.length && document.visibilityState === 'hidden') void this.flush();
    }
  }

  private uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private getSelector(el: HTMLElement): string {
    if (!el) return '';
    
    // If element has an ID, that's usually enough
    if (el.id) return `#${el.id}`;
    
    // If it has a test ID, use that
    if (el.getAttribute('data-testid')) return `[data-testid="${el.getAttribute('data-testid')}"]`;
    
    const path: string[] = [];
    let current: HTMLElement | null = el;
    
    while (current && current.nodeType === Node.ELEMENT_NODE && current !== document.body && current !== document.documentElement) {
        let selector = current.tagName.toLowerCase();
        
        if (current.id) {
            selector = `#${current.id}`;
            path.unshift(selector);
            break; // IDs are unique, we can stop here
        } else {
            let sibling = current;
            let nth = 1;
            while (sibling = sibling.previousElementSibling as HTMLElement) {
                if (sibling.tagName === current.tagName) nth++;
            }
            if (nth > 1) selector += `:nth-of-type(${nth})`;
        }
        
        if (current.className && typeof current.className === 'string') {
             // Clean classes: remove dynamic/tailwind utility classes to avoid breakage on styles changes
             // We'll keep layout-related ones if they look stable, or just stick to component classes
             // For now, let's keep it simple: take the first 2 classes that aren't obviously state-based
             const classes = current.className.trim().split(/\s+/).filter(c => 
                c && 
                !c.includes(':') && 
                !c.startsWith('hover') && 
                !c.startsWith('focus') && 
                !c.startsWith('active')
             ); 
             
             if (classes.length > 0) {
                 selector += `.${classes.slice(0, 1).join('.')}`; // Use just the first significant class
             }
        }

        path.unshift(selector);
        current = current.parentElement;
        if (path.length > 4) break; // Limit depth to avoid massive selectors
    }
    
    return path.join(' > ');
  }

  private getDeviceType(): string {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) return "tablet";
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) return "mobile";
    return "desktop";
  }

  private checkRageClick(selector: string, x: number, y: number) {
      const now = Date.now();
      const clicks = this.clickHistory.get(selector) || [];
      
      // Filter clicks within window
      const recentClicks = clicks.filter(t => now - t < this.RAGE_CLICK_WINDOW);
      recentClicks.push(now);
      this.clickHistory.set(selector, recentClicks);
      
      if (recentClicks.length === this.RAGE_CLICK_THRESHOLD) {
          this.track('rage_click', { 
              selector,
              x, 
              y,
              count: recentClicks.length 
          });
          // Reset to avoid duplicate rage clicks for the same sequence
          this.clickHistory.set(selector, []); 
      }
  }
}

if (typeof window !== 'undefined') {
  const qct = new QCTTracker();
  (window as unknown as { qct: QCTTracker }).qct = qct;
}
